import { useTranslation } from 'react-i18next';
import type { DebtAgingBucketDto } from '../../api/reports';
import { formatMoney } from '../../utils/formatMoney';

/**
 * Stacked aging bar for receivables/payables. The buckets form an ORDERED
 * severity scale (current → 90+ days overdue), so the color job is SEQUENTIAL:
 * one hue (brand navy), light → dark, verified strictly monotonic in OKLab
 * lightness (0.827 → 0.235). The two lightest steps sit below 3:1 contrast
 * against the surface, so every segment is also labeled in the bucket table
 * below the bar (the accessible table view) and carries a hover tooltip —
 * identity never depends on color alone. 2px surface gaps separate segments.
 */

// Brand navy steps (global.css @theme): primary-200..600, light → dark.
const RAMP = ['#b5c8e3', '#90add5', '#648cc4', '#2d5386', '#071e3b'];
// Steps dark enough for white labels (OKLab L ≲ 0.45); light steps wear ink.
const DARK_STEPS = new Set([3, 4]);

interface AgingBarProps {
  buckets: DebtAgingBucketDto[];
  total: number;
  onSelectBucket?: (daysFrom: number) => void;
}

function bucketLabel(bucket: DebtAgingBucketDto, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (bucket.daysFrom <= 0) return t('analytics.aging.current');
  if (bucket.daysFrom >= 91) return t('analytics.aging.over90');
  return t('analytics.aging.range', { from: bucket.daysFrom, to: bucket.daysTo });
}

export default function AgingBar({ buckets, total, onSelectBucket }: AgingBarProps) {
  const { t } = useTranslation();
  const safeTotal = total > 0 ? total : 1;

  return (
    <div>
      {/* Stacked bar — segments sized by share, 2px surface gaps between fills */}
      <div className="flex h-9 w-full gap-[2px] bg-white" role="img" aria-label={t('analytics.aging.ariaLabel')}>
        {buckets.map((bucket, i) => {
          const widthPct = (bucket.amount / safeTotal) * 100;
          if (widthPct < 0.1) return null;
          const isDark = DARK_STEPS.has(i);
          const label = formatMoney(bucket.amount, false);
          return (
            <div
              key={bucket.daysFrom}
              className={`relative flex items-center justify-center min-w-[3px] transition-opacity hover:opacity-80 ${
                onSelectBucket ? 'cursor-pointer' : ''
              } ${i === 0 ? 'rounded-l' : ''} ${i === buckets.length - 1 ? 'rounded-r' : ''}`}
              style={{ width: `${widthPct}%`, backgroundColor: RAMP[i] }}
              onClick={onSelectBucket ? () => onSelectBucket(bucket.daysFrom) : undefined}
              title={`${bucket.bucketName}: ${formatMoney(bucket.amount)} (${bucket.personCount})`}
            >
              {widthPct >= 9 && (
                <span className={`text-[11px] font-medium tabular-nums truncate px-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {label}
                </span>
              )}
            </div>
          );
        })}
        {total <= 0 && (
          <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded">
            {t('analytics.aging.empty')}
          </div>
        )}
      </div>

      {/* Legend + accessible table view: every bucket named, valued, clickable */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {buckets.map((bucket, i) => (
          <button
            key={bucket.daysFrom}
            onClick={onSelectBucket ? () => onSelectBucket(bucket.daysFrom) : undefined}
            className={`flex items-center gap-2 text-left px-2 py-1.5 rounded-md border border-gray-100 bg-white text-xs hover:border-primary-300 transition-colors ${
              onSelectBucket ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: RAMP[i] }} />
            <span className="min-w-0">
              <span className="block text-gray-500 truncate">{bucketLabel(bucket, t)}</span>
              <span className="block font-medium tabular-nums text-gray-900">
                {formatMoney(bucket.amount)} <span className="text-gray-400">· {bucket.personCount}</span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
